import { DropdownOption } from 'components/final-form/dropdownUtils';
import LoaderComponent from 'components/LoaderComponent';
import _ from 'lodash';
import React, { Component } from 'react';
import { FieldRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Dropdown, DropdownItemProps, DropdownProps, Form } from 'semantic-ui-react';
import { DropdownOnSearchChangeData } from 'semantic-ui-react/dist/commonjs/modules/Dropdown/Dropdown';
import styled from 'styled-components';
import { debounce } from 'ts-debounce';
import { isValueEmpty } from 'utils/validatorUtils';

export const PxDropdownSc = styled(Dropdown)`

  &.ui.selection.dropdown {
    min-height: 29px;
    //max-height: 30px;
    padding: 5px;
  }

  &.ui.selection.dropdown > .dropdown.icon {
    top: 60%;
    padding: 0 10px;
  }

  &.ui.search.dropdown > .text {
    line-height: 1.21428571em;
  }

  &.ui.search.dropdown .menu {
    max-height: 19.028571rem;
  }

  &.ui.search.dropdown .menu>.item.inactive {
    color: #a3a3a3;

    i.ban.grey.icon {
      color: #a3a3a3 !important;
    }
  }

  &.ui.multiple.dropdown > .label {
    margin: 5px 3px;
  }

  &.ui.search.dropdown > input.search {
    padding: 5px;
    margin: 0;
  }

`;

export interface FinalFormSelectProps extends FieldRenderProps, DropdownProps, MessageSourceProps {
  openOnFocus?: boolean,
  remoteSearch?: boolean,
  customRemoteSearchMethod?: (search: string) => void,
  remoteSearchMethod?: (search: string) => Promise<Array<any>>,
  searchResultToOptionMapper?: (options: Array<any>) => Array<DropdownOption>,
  onNewAddition?: (addition: DropdownOption) => void,
  customOnChange?: (value: any) => void
}

interface State {
  options: Array<DropdownItemProps>
}

class FinalFormSelectUI extends Component<FinalFormSelectProps, State> {

  constructor(props: FinalFormSelectProps) {
    super(props);

    const {
      allowAdditions,
      defaultValue,
      options: propsOptions
    } = props;

    let options = propsOptions || [];
    if (allowAdditions
        && defaultValue
        && !_.isArray(defaultValue)
        && (!options || options.length === 0)) {

      const defVal = defaultValue as (string | number | boolean);
      options = [
        {
          key: defVal,
          text: defVal.toString(),
          value: defVal
        }
      ];
    }

    this.state = {
      options: options
    }
  }

  componentDidUpdate(
      prevProps: Readonly<FinalFormSelectProps>,
      prevState: Readonly<State>, snapshot?: any
  ): void {

    const {options: optionsProps = []} = this.props;
    const {options: prevOptions = []} = prevProps;

    if ((prevOptions !== optionsProps && optionsProps.length != 0 && prevOptions.length != 0)
        || prevOptions.length != optionsProps.length) {
      this.setState({
        options: optionsProps
      });
    }
  }

  onSearchChange = debounce((
      e: React.SyntheticEvent<HTMLElement>,
      {searchQuery}: DropdownOnSearchChangeData
  ) => {

    const {
      remoteSearchMethod,
      customRemoteSearchMethod,
      searchResultToOptionMapper,
      allowAdditions,
      getMessage
    } = this.props;

    if (!customRemoteSearchMethod) {
      if (remoteSearchMethod) {
        if (!isValueEmpty(searchQuery)) {

          this.setState({
            options: [{
              key: "dummy",
              text: "dummy",
              value: "dummy",
              content: (
                  <div style={{height: allowAdditions ? "163px" : "200px"}}>
                    <LoaderComponent message={getMessage("loadingData")}/>
                  </div>
              ),
              disabled: true
            }]
          });

          remoteSearchMethod(searchQuery)
              .then(results => {
                this.setState({
                  options: searchResultToOptionMapper ? searchResultToOptionMapper(results) : results
                })
              })
        } else {
          this.setState({
            options: []
          })
        }
      }
    } else {

      this.setState({
        options: [{
          key: "dummy",
          text: "dummy",
          value: "dummy",
          content: (
              <div style={{height: allowAdditions ? "163px" : "200px"}}>
                <LoaderComponent message={getMessage("loadingData")}/>
              </div>
          ),
          disabled: true
        }]
      });

      customRemoteSearchMethod(searchQuery);
    }
  }, 200);

  onValueChange = (event: React.SyntheticEvent<HTMLElement>, {value}: DropdownProps) => {

    const {input, remoteSearch, clearable, customOnChange} = this.props;

    if (remoteSearch && clearable && !value) {
      this.setState({
        options: []
      })
    }

    input.onChange(value);
    if (customOnChange) {
      customOnChange(value);
    }
  };

  removeAutoCompleteOnFocus = (e: any) => {
    e.target.setAttribute("autocomplete", "nope");
  };

  customSearch = (options: Array<DropdownItemProps>): Array<DropdownItemProps> => {

    const {allowAdditions} = this.props;

    if (allowAdditions) {
      return options.filter(option => {
        return option.key != "addition";
      });
    }

    return options;
  };

  onNewAddition = (e: any, {value}: any): void => {

    const {onNewAddition} = this.props;

    const newAddition = {
      key: value,
      text: value.toString(),
      value: value
    };

    if (onNewAddition) {
      onNewAddition(newAddition);
    }

    this.setState({
      options: [newAddition, ...this.state.options]
    });
  };

  render(): React.ReactNode {

    const {
      meta,
      input,
      allowAdditions,
      search,
      remoteSearch,
      remoteSearchMethod,
      customRemoteSearchMethod,
      searchResultToOptionMapper,
      automaticAddingOfValue,
      automaticAdditionMapper,
      getMessage,
      getMessageWithNamedParams,
      multiple,
      onNewAddition,
      openOnFocus = true,
      ...restProps
    } = this.props;

    const {options} = this.state;
    const value = multiple && input.value === "" ? [] : input.value;

    const restPropsCleaned = _.omit(restProps, ["children", "className", "customOnChange"]);
    const optionsCleaned = options.map(option => _.omit(option, ["predefinedOption"]));

    return (
        <Form.Field className={`${this.props.className}`}>
          <>
            <PxDropdownSc
                error={!!(meta.touched && meta.error)}
                selection
                value={value}
                onChange={this.onValueChange}
                onFocus={this.removeAutoCompleteOnFocus}
                multiple={!!multiple}
                {...restPropsCleaned}
                search={remoteSearch ? this.customSearch : true}
                allowAdditions={allowAdditions}
                onAddItem={allowAdditions ? this.onNewAddition : undefined}
                onSearchChange={remoteSearch ? this.onSearchChange : undefined}
                options={optionsCleaned}
                openOnFocus={openOnFocus}
            />
            {this.props.children}
          </>
        </Form.Field>
    )
  }

}

const FinalFormSelect = withMessages(FinalFormSelectUI);
export default FinalFormSelect;
